import {
  openModal,
  routeTo,
  routeBack,
  finish,
  partiallyUpdate
} from '../../core/actions';
import FlowHeader from '../../components/FlowHeader/FlowHeader';
import FooterSite from '../../components/FooterSite/FooterSite';
import BaseLayout from '../../components/BaseLayout/BaseLayout';
import SummitRewards from '../../components/BaseLayout/SummitRewards';
import InvestmentOptionsContents from '../../components/InvestmentOptions/Contents';
import {
  hasURLTickers,
  isValidEmail,
  isValidMaskedSSN,
  usernameValidation,
  passwordValidation,
  isValidDob,
  chose,
  isReviewMode,
  existingUserShouldGotoLogin,
  existingUserShouldGotoEnablement,
  isUsernameAlreadyTaken,
  choseResultingPath,
  userValidationFailed,
  isAuthenticated,
  compose,
  chosenAddressValidation,
  isRDFPath,
  isPageClick,
  rdfRecommendationDiffers,
  meetsMaximumPriorYearInvestment,
  meetsMaximumCurrentYearInvestment,
  guestUserLoginRequired,
  showUpdateToIClassModal
} from '../shared/testers';

import ContribChecklistScreen from '../../screens/ChecklistScreen/ContribChecklistScreen';
import ContributoryProfileScreen from '../../screens/ProfileScreen/ContributoryProfileScreen';
import PersonalInformationScreen from '../../screens/PersonalInformationScreen/PersonalInformationScreen';
import CreateCredentialsScreen from '../../screens/CreateCredentialsScreen/CreateCredentialsScreen';
import ContactInformationScreen from '../../screens/ContactInformationScreen/ContactInformationScreen';
import ReviewScreen from '../../screens/ReviewScreen/ReviewScreen';
import ConfirmationScreen from '../../screens/ConfirmationScreen/ConfirmationScreen';
import InvestmentReviewScreen from '../../screens/InvestmentReviewScreen/InvestmentReviewScreen';
import BYOPScreen from '../../screens/BYOPScreen/BYOPScreen';
import UserValidationModal from '../../components/UserValidationModal/UserValidationModal';
import DifferentRDFModal from '../../components/DifferentRDFModal/DifferentRDFModal';
import makeForcedLoginModal from '../../components/ForcedLoginModal/ForcedLoginModal';
import GuestUserLoginModal from '../../components/GuestUserLoginModal/GuestUserLoginModal';
import AddressValidationModal from '../../components/AddressValidationModal/AddressValidationModal';
import CredentialsValidationModal from '../../components/CredentialsValidationModal/CredentialsValidationModal';
import RDFScreen from '../../screens/RDFScreen/RDFScreen';
import RDFTermsScreen from '../../screens/RDFTermsScreen/RDFTermsScreen';
import InvestmentOptionsScreen from '../../screens/InvestmentOptionsScreen/InvestmentOptionsScreen';
import MoneyMarketScreen from '../../screens/MoneyMarketScreen/MoneyMarketScreen';
import UpdateToIClassModal from '../../components/UpdateToIClass/UpdateToIClassModal';

import {
  loadLoginData,
  commitProfileData,
  commitNewInvestmentsRDF,
  commitNewInvestmentsBYOP,
  loadContributoryLimits,
  makeLoadProfileData,
  updateAnalytics,
  createApplication,
  loadTermsAndContributionPeriod
} from './loaders';
import {
  commitContactInformation,
  commitPersonalInformation,
  commitReferrerForm,
  loadTermsForSubmission,
  registerAndLoginUser,
  loadTermsForRDF,
  commitRDFTermsConsent,
  loadRDFiClassData,
  commitShoppingFunds,
  commitMoneyMarketFund,
  loadMoneyMarketData
} from '../shared/loaders';
import { FlowNode } from '../../core/types/flow';
import {
  contactTextFn,
  redirectToCLX,
  redirectToEnablement
} from '../shared/utils';
import { updateJourneyProfile } from '../return-journey/loaders';
import { IRAAccountTypes } from '../transfer-flows/loaders';

export const makeNodes = (
  iraAccountTypes: IRAAccountTypes
): Array<FlowNode> => [
  {
    route: 'landing',
    screen: ContribChecklistScreen,
    load: loadLoginData,
    commit: commitShoppingFunds,
    next: {
      default: routeTo('profile')
    },
    flowProps: {
      displayStickyBanner: true,
      getContactText: contactTextFn(),
      iraAccountType: iraAccountTypes,
      analytics: {
        pageName: 'account-info'
      }
    }
  },
  {
    route: 'profile',
    screen: ContributoryProfileScreen,
    load: makeLoadProfileData(iraAccountTypes),
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'primaryEmailAddress',
        required: true,
        validation: isValidEmail
      },
      {
        name: 'dob',
        required: true,
        validation: isValidDob
      },
      {
        name: 'priorYearAmount',
        validation: [meetsMaximumPriorYearInvestment]
      },
      {
        name: 'currentYearAmount',
        validation: [meetsMaximumCurrentYearInvestment]
      }
    ],
    startsPersistingSensitive: true,
    commit: commitProfileData,
    next: {
      cases: [
        [hasURLTickers, routeTo('byop/review')],
        [chose('skipLogin'), routeTo('investment')],
        [guestUserLoginRequired, openModal(GuestUserLoginModal)]
      ],
      default: routeTo('investment')
    },
    flowProps: {
      getContactText: contactTextFn(),
      analytics: {
        pageName: 'About'
      }
    }
  },
  {
    route: 'investment',
    screen: InvestmentOptionsScreen,
    next: {
      cases: [
        [chose('byop'), routeTo('byop')],
        [chose('money-market'), routeTo('money-market')],
        [chose('rdf'), routeTo('rdf/terms')]
      ],
      default: routeTo('byop')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'wide',
      title: 'How would you like to choose funds for your IRA?',
      options: InvestmentOptionsContents,
      noAPPCard: true,
      analytics: {
        pageName: 'InvestmentOptions'
      }
    }
  },
  {
    route: 'byop',
    screen: BYOPScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [choseResultingPath('updateAndBack'), routeBack()]
      ],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'BuildPortfolio'
      }
    }
  },
  {
    route: 'money-market',
    screen: MoneyMarketScreen,
    load: loadMoneyMarketData,
    commit: commitMoneyMarketFund,
    next: {
      cases: [[chose('viewAllMoneyMarketFunds'), routeTo('byop')]],
      default: routeTo('byop/review')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'MoneyMarketFund'
      }
    }
  },
  {
    route: 'byop/review',
    screen: InvestmentReviewScreen,
    commit: commitNewInvestmentsBYOP,
    next: {
      cases: [
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)],
        [isReviewMode(), routeTo('review')],
        [choseResultingPath('updateAndBack'), routeBack()],
        [choseResultingPath('reselect-funds'), routeTo('investment')]
      ],
      default: routeTo('information')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'wide',
      analytics: {
        pageName: 'BuildPortfolioSummary'
      }
    }
  },
  {
    route: 'rdf/terms',
    form: [
      {
        name: 'electronicAccessConsent',
        type: 'checkbox'
      },
      {
        name: 'advisoryServiceConsent',
        type: 'checkbox'
      }
    ],
    screen: RDFTermsScreen,
    load: loadTermsForRDF,
    commit: commitRDFTermsConsent,
    next: {
      default: routeTo('rdf/selection')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      analytics: {
        pageName: 'RetirementDateFundConsent'
      }
    }
  },
  {
    route: 'rdf/selection',
    screen: RDFScreen,
    load: loadRDFiClassData,
    commit: commitNewInvestmentsRDF,
    next: {
      cases: [
        [chose('byop'), routeTo('byop')],
        [isReviewMode(), routeTo('review')]
      ],
      default: routeTo('information')
    },
    flowProps: {
      getContactText: contactTextFn('Investment'),
      layoutClass: 'medium',
      analytics: {
        pageName: 'RetirementDateFundSelection'
      }
    }
  },
  {
    route: 'information',
    screen: PersonalInformationScreen,
    form: [
      {
        name: 'firstName',
        required: true
      },
      {
        name: 'lastName',
        required: true
      },
      {
        name: 'middleName'
      },
      { name: 'citizenship', required: true },
      {
        name: 'dob'
      },
      {
        name: 'ssn',
        required: true,
        validation: [isValidMaskedSSN]
      }
    ],
    commit: commitPersonalInformation,
    load: loadContributoryLimits,
    next: {
      cases: [
        [userValidationFailed, openModal(UserValidationModal)],
        [
          compose(isRDFPath, isPageClick, rdfRecommendationDiffers),
          openModal(DifferentRDFModal)
        ],
        [
          existingUserShouldGotoEnablement,
          openModal(makeForcedLoginModal(redirectToEnablement))
        ],
        [
          existingUserShouldGotoLogin,
          openModal(makeForcedLoginModal(redirectToCLX))
        ],
        [isReviewMode(), routeTo('review')],
        [showUpdateToIClassModal, openModal(UpdateToIClassModal)]
      ],
      default: routeTo('contact/information')
    },
    flowProps: {
      getContactText: contactTextFn('contributoryPICI'),
      analytics: {
        pageName: 'PersonalInformation'
      },
      fullScreenModal: compose(isRDFPath, isPageClick, rdfRecommendationDiffers)
    }
  },
  {
    route: 'contact/information',
    screen: ContactInformationScreen,
    load: loadTermsForSubmission,
    form: [
      { name: 'residentialStreet1', required: true },
      { name: 'residentialStreet2' },
      { name: 'residentialZip', required: true },
      { name: 'residentialCity', required: true },
      { name: 'residentialState', required: true },
      { name: 'mailingStreet1', required: true },
      { name: 'mailingStreet2' },
      { name: 'mailingZip', required: true },
      { name: 'mailingCity', required: true },
      { name: 'mailingState', required: true },
      { name: 'primaryEmailAddress', required: true },
      { name: 'phone1', required: true },
      { name: 'phone2' },
      { name: 'phone3' },
      { name: 'mailingAddressDifferent', type: 'checkbox' },
      { name: 'useResidentialAddress', required: true },
      { name: 'useMailingAddress', required: true }
    ],
    commit: commitContactInformation,
    next: {
      cases: [
        [chosenAddressValidation, openModal(AddressValidationModal)],
        [isReviewMode(), routeTo('review')],
        [isAuthenticated(), routeTo('review')]
      ],
      default: routeTo('credentials')
    },
    flowProps: {
      getContactText: contactTextFn('contributoryPICI'),
      analytics: {
        pageName: 'ContactInformation'
      }
    }
  },
  {
    route: 'credentials',
    form: [
      {
        name: 'username',
        required: true,
        validation: usernameValidation
      },
      {
        name: 'password',
        required: true,
        validation: passwordValidation
      }
    ],
    screen: CreateCredentialsScreen,
    commit: registerAndLoginUser,
    next: {
      cases: [[isUsernameAlreadyTaken, openModal(CredentialsValidationModal)]],
      default: routeTo('review')
    },
    flowProps: {
      getContactText: contactTextFn('PICI'),
      analytics: {
        pageName: 'Enablement'
      }
    }
  },
  {
    route: 'review',
    screen: ReviewScreen,
    load: loadTermsAndContributionPeriod,
    commit: createApplication,
    next: {
      cases: [
        [chose('updateCustomer'), partiallyUpdate()],
        [chose('byop'), routeTo('byop')],
        [chose('info'), routeTo('information')],
        [chose('contact'), routeTo('contact/information')],
        [chose('rdf'), routeTo('rdf/selection')]
      ],
      default: routeTo('confirmation')
    },
    flowProps: {
      getContactText: contactTextFn('contributoryReview'),
      analytics: {
        pageName: 'ReviewConfirm'
      }
    }
  },
  {
    route: 'confirmation',
    screen: ConfirmationScreen,
    commit: commitReferrerForm,
    next: {
      cases: [[chose('referrer'), partiallyUpdate()]],
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'Confirmation',
        isComplete: true
      }
    }
  }
];

export const makeFlow = (
  path: string,
  iraAccountTypes: IRAAccountTypes
): any => {
  const nodes = makeNodes(iraAccountTypes);
  return {
    namespace: `mutual-fund/retirement/contributory/${path}`,
    nodes,
    start: 'landing',
    pageDecorators: [BaseLayout, SummitRewards],
    onLoad: updateAnalytics,
    onCommit: updateJourneyProfile,
    flowProps: {
      getContactText: contactTextFn(),
      header: FlowHeader,
      footer: FooterSite
    }
  };
};
